import React, { FC, useEffect } from 'react'
import styles from 'styles/components/template.module.scss'
import { Headline, MainHeadline, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { TextInput } from 'components/Form'
import { useString } from 'hooks/form'
import { BlueButton, NavButton, OrangeButton } from 'components/Button'
import { initShop, saveShop } from 'reducks/shop/operations'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { push } from 'connected-react-router'
import { initStore } from 'reducks/store/operations'
import { initConfirm } from 'reducks/confirm/operations'
import { ShopAccountError } from 'components/Error'

type Props = {
  isCreatePage: boolean
  isEditedByOwner: boolean
  shopId?: string
}

const ShopEditAccountTemplate: FC<Props> = (props) => {
  const { isCreatePage, isEditedByOwner, shopId } = props

  const dispatch = useDispatch()

  const shop = useSelector((state: State) => state.shop)

  const {
    handleState: handleStripeAccountId,
    setState: setStripeAccountId,
    state: stripeAccountId,
  } = useString<string>('')
  const { handleState: handleName, setState: setName, state: name } = useString<string>('')
  const { handleState: handleContact, setState: setContact, state: contact } = useString<string>('')
  const {
    handleState: handleResponsiblePerson,
    setState: setResponsiblePerson,
    state: responsiblePerson,
  } = useString<string>('')
  const {
    handleState: handleInvoiceBusinessName,
    setState: setInvoiceBusinessName,
    state: invoiceBusinessName,
  } = useString<string>('')
  const { handleState: handleInvoiceNumber, setState: setInvoiceNumber, state: invoiceNumber } = useString<string>('')
  const { setState: setStripeCustomerId, state: stripeCustomerId } = useString<string>('')

  const isFilledRequired = name !== '' && contact !== '' && responsiblePerson !== ''

  const handleSubmit = () =>
    dispatch(
      saveShop(
        {
          name: name,
          contact: contact,
          responsible_person: responsiblePerson,
          stripe_account_id: stripeAccountId,
          stripe_customer_id: shop.stripe_customer_id ?? '',
          stripe_payment_method_id: shop.stripe_payment_method_id ?? '',
          invoice_business_name: invoiceBusinessName,
          invoice_number: invoiceNumber,
        },
        shopId,
        isEditedByOwner
      )
    )

  useEffect(() => {
    if (!shop) return
    setStripeAccountId(shop.stripe_account_id ?? '')
    setName(shop.name ?? '')
    setContact(shop.contact ?? '')
    setResponsiblePerson(shop.responsible_person ?? '')
    setInvoiceBusinessName(shop.invoice_business_name ?? '')
    setInvoiceNumber(shop.invoice_number ?? '')
    setStripeCustomerId(shop.stripe_customer_id ?? '')
  }, [shop])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={isEditedByOwner ? '特定商取引法に関する記載' : '店舗アカウント情報の設定'} />
      <Spacer size={28} />
      {!isEditedByOwner && (
        <>
          <Headline headline={'Stripeアカウント'} />
          <Spacer size={20} />
          <TextInput
            disabled={false}
            label={'stripe アカウントID'}
            onChange={handleStripeAccountId}
            placeholder={'入力してください'}
            required={false}
            size={'small'}
            type={'text'}
            value={stripeAccountId}
          />
          <Spacer size={20} />
          <TextInput
            disabled={true}
            label={'stripe カスタマーID '}
            required={false}
            size={'small'}
            type={'text'}
            value={stripeCustomerId || 'カード情報が登録されていません'}
          />
          <Spacer size={28} />
        </>
      )}
      <Headline headline={'特定商取引法に関する記載'} />
      <Spacer size={20} />
      <TextInput
        disabled={false}
        id={'name'}
        label={'対象店舗'}
        onChange={handleName}
        placeholder={'株式会社sacri'}
        required={true}
        size={'small'}
        type={'text'}
        value={name}
      />
      <SubDescription
        description={
          '※法人の場合は、【株式会社◯◯／合同会社◯◯】 個人事業主、屋号ありの場合は【◯◯ベーカリー　◯◯花子】と入れてください。'
        }
      />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'contact'}
        label={'販売業者の住所および連絡先'}
        onChange={handleContact}
        placeholder={'入力してください'}
        required={true}
        size={'small'}
        type={'text'}
        value={contact}
      />
      <SubDescription description={'※住所もしくは、電話番号を入力してください。'} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'responsible-person'}
        label={'販売業者の代表者または販売責任者'}
        onChange={handleResponsiblePerson}
        placeholder={'入力してください'}
        required={true}
        size={'small'}
        type={'text'}
        value={responsiblePerson}
      />
      <SubDescription description={'※【代表取締役 ◯◯太郎】や 【販売責任者 ◯◯花子】等で記入してください。'} />
      <Spacer size={28} />
      <Headline headline={'インボイスに関する記載'} />
      <Spacer size={20} />
      <SubDescription description={'領収書に記載されます。'} />
      <Spacer size={20} />
      <TextInput
        disabled={false}
        id={'invoice-business-name'}
        label={'適格請求書発行事業者の氏名または名称'}
        onChange={handleInvoiceBusinessName}
        placeholder={'株式会社sacri'}
        required={false}
        size={'small'}
        type={'text'}
        value={invoiceBusinessName}
      />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'invoice-number'}
        label={'登録番号'}
        maxLength={14}
        onChange={handleInvoiceNumber}
        placeholder={'T0123456789012'}
        required={false}
        size={'small'}
        type={'text'}
        value={invoiceNumber}
      />
      <Spacer size={48} />
      {isCreatePage ? (
        <BlueButton
          disabled={!isFilledRequired}
          label={'基本情報の登録へ進む'}
          onClick={() =>
            dispatch(
              saveShop(
                {
                  name: name,
                  contact: contact,
                  responsible_person: responsiblePerson,
                  stripe_account_id: stripeAccountId,
                  stripe_customer_id: shop.stripe_customer_id ?? '',
                  stripe_payment_method_id: shop.stripe_payment_method_id ?? '',
                  invoice_business_name: invoiceBusinessName,
                  invoice_number: invoiceNumber,
                },
                undefined,
                isEditedByOwner
              )
            )
          }
          endIconPath={'/icon/white/arrow_next_small.svg'}
          size={'medium'}
          type={'main'}
        />
      ) : (
        <OrangeButton
          disabled={!isFilledRequired}
          label={'保存する'}
          onClick={handleSubmit}
          size={'medium'}
          type={'main'}
        />
      )}
      {!isFilledRequired && (
        <>
          <Spacer size={24} />
          <ShopAccountError name={name} contact={contact} responsiblePerson={responsiblePerson} />
        </>
      )}
      <Spacer size={48} />
      <NavButton
        label={'トップページに戻る'}
        iconPath={'/icon/grey/home.svg'}
        onClick={() => {
          if (isEditedByOwner) {
            dispatch(push('/'))
          } else {
            dispatch(push('/admin/shops'))
            dispatch(initConfirm())
            dispatch(initStore())
            dispatch(initShop())
          }
        }}
        url={''}
      />
      <Spacer size={72} />
    </div>
  )
}

export default ShopEditAccountTemplate
